import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { HTTP_UNAUTHORIZED } from "@/lib/constants";
import AdminTable from "@/pages/admin/AdminTable";
import EstimatorAnalytics from "@/pages/admin/EstimatorAnalytics";
import ConsentHistoryPanel from "@/pages/admin/ConsentHistoryPanel";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/site/SEO";

export default function Admin() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [apps, setApps] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [consentAppId, setConsentAppId] = useState(null);

  const copyBrokerLink = useCallback(async (appId) => {
    try {
      const { data } = await api.get(`/admin/broker-verifications/${appId}`);
      const url = `${window.location.origin}/broker-verify/${data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Broker verification link copied to clipboard.");
    } catch (err) {
      toast.error("Could not fetch broker verification link.");
    }
  }, []);

  const bounceToLogin = useCallback(async () => {
    toast.error("Session expired. Please sign in again.");
    await logout();
    nav("/login", { replace: true });
  }, [logout, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/applications");
      setApps(data);
    } catch (err) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        toast.error("Session expired. Please sign in again.");
        await logout();
        nav("/login", { replace: true });
      } else {
        toast.error("Failed to load applications.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout, nav]);

  useEffect(() => { load(); }, [load]);

  const onLogout = async () => { await logout(); nav("/", { replace: true }); };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      setApps((a) => a.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Status updated to ${status}`);
    } catch { toast.error("Failed to update"); }
  };

  const visible = apps.filter((a) => {
    const q = search.toLowerCase();
    const matchQ = !q ||
      `${a.first_name} ${a.last_name}`.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q);
    const matchF = filter === "all" || a.status === filter;
    return matchQ && matchF;
  });

  return (
    <div className="min-h-screen bg-[#FCFAF8]" data-testid="admin-page">
      <SEO title="Admin — Applications" description="Admin dashboard for reviewing commission advance applications." path="/admin/applications" />
      <header className="bg-white border-b border-[#E8DFD8]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_agent-funding-pro/artifacts/arvd4npc_image.jpeg"
              alt="Hannah Howard Funding"
              className="w-10 h-10 rounded-md object-cover"
            />
            <div>
              <span className="font-serif-lux text-2xl">Hannah Howard <span className="text-[#8C7A70]">Admin</span></span>
              <p className="text-xs text-[#8C7A70]">{user?.email}</p>
            </div>
          </div>
          <button onClick={onLogout} className="btn-ghost text-sm py-2 px-4" data-testid="admin-logout">
            <LogOut size={16} strokeWidth={1.5}/> Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <EstimatorAnalytics onUnauthorized={bounceToLogin} />
        <AdminTable
          loading={loading}
          search={search} setSearch={setSearch}
          filter={filter} setFilter={setFilter}
          visible={visible}
          onStatusChange={updateStatus}
          onViewConsents={setConsentAppId}
          onCopyBrokerLink={copyBrokerLink}
        />
      </main>
      {consentAppId ? (
        <ConsentHistoryPanel
          applicationId={consentAppId}
          onClose={() => setConsentAppId(null)}
          onUnauthorized={bounceToLogin}
        />
      ) : null}
    </div>
  );
}
